'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faSearch,
  faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useAuthorization } from 'src/hooks/authorization';
import { useGetCompanyRolesQuery } from 'src/api/redux';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useSettings } from 'src/components/Settings/hooks';
import {
  Button,
  Drawer,
  Typography,
  Select,
  Option,
  Search,
} from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import {
  filterAssignableRoles,
  isCustomRole,
} from 'src/components/Settings/Companies/components/RolesAdmin/utils';
import { CustomRoleChip } from 'src/components/Settings/Companies/components/RolesAdmin/components/CustomRoleChip';
import { bulkRoleAdd } from 'src/api/user/usersApi';

const BulkRoleAddDrawer = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const { userManagementCapabilities } = useAuthorization();
  const { isStaff } = userManagementCapabilities;
  const { selectedUsers, setSelectedUsers } = useSettings();
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: roles } = useGetCompanyRolesQuery(
    { companyId },
    { skip: !companyId }
  );

  // Bulk Exporter is MedScout's to grant, so a customer cannot bulk-assign it
  // (MKT-527).
  const assignableRoles = useMemo(
    () => filterAssignableRoles(roles?.results, isStaff),
    [roles?.results, isStaff]
  );

  const handleCancel = useCallback(() => {
    closeDrawer('bulk-role-add');
    setSelectedUsers?.([]);
  }, [closeDrawer, setSelectedUsers]);

  const handleAdd = useCallback(async () => {
    try {
      setIsLoading(true);
      await bulkRoleAdd({
        companyId,
        roleId: selectedRole,
        userIds: selectedUsers.map((user) => user.id?.toString()),
      });
      setNotification({
        title: 'Success',
        message: `Role added to ${selectedUsers.length} users`,
        type: 'success',
      });
    } catch (err) {
      log.exception(`Failed to add role to users: ${err.error_message}`);
      setNotification({
        title: 'Error',
        message: err.error_message || 'Failed to add role',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      closeDrawer('bulk-role-add');
      setSelectedUsers?.([]);
      setSelectedRole('');
    }
  }, [
    companyId,
    selectedRole,
    selectedUsers,
    setNotification,
    log,
    closeDrawer,
    setSelectedUsers,
  ]);

  const handleRoleChange = useCallback(
    (event: React.MouseEvent, value: string) => {
      setSelectedRole(value);
    },
    []
  );

  const handleRemoveUser = useCallback(
    (user: MedScout.User) => {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    },
    [selectedUsers, setSelectedUsers]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch]
  );

  const isDirty = useMemo(() => {
    return selectedRole !== '' && selectedUsers.length > 0;
  }, [selectedRole, selectedUsers]);

  const filteredUsers = useMemo(() => {
    return selectedUsers.filter((user) =>
      user.username.toLowerCase().includes(search.toLowerCase())
    );
  }, [selectedUsers, search]);

  return (
    <Drawer
      isOpen={isDrawerOpen('bulk-role-add')}
      onClose={handleCancel}
      size="lg"
      hideBackdrop
      className="overflow-hidden flex flex-col h-full"
    >
      <Drawer.Header
        className="bg-sky-100 text-primary border-b-0"
        data-testid="bulk-role-add-header"
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p className="font-bold text-lg" data-testid="bulk-role-add-title">
              Bulk Role Assignment
            </p>
            <div className="text-sm text-primary/70">
              {selectedUsers.length} users selected
            </div>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleCancel}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
              data-testid="bulk-role-add-close-button"
            />
          </div>
        </div>
      </Drawer.Header>

      <Drawer.Content className="bg-neutral-50 flex-1 overflow-hidden">
        <div className="flex flex-col h-full">
          <div className="p-4 space-y-6 flex-none">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Typography className="text-sm font-medium text-neutral-700">
                    Select Role
                  </Typography>
                  <Select
                    size="md"
                    value={selectedRole}
                    onChange={handleRoleChange}
                    className="w-full rounded-full shadow-sm border-neutral-200 focus:ring-sky-500 bg-white"
                  >
                    {assignableRoles.map((role) => (
                      <Option key={role.id} value={role.id}>
                        {/* The label keeps its own element so it stays the
                            option's text, chip or no chip. */}
                        <span className="flex items-center gap-2">
                          <span className="truncate">{role.label}</span>
                          {isCustomRole(role) && (
                            <CustomRoleChip
                              withTooltip={false}
                              data-testid={`custom-role-chip-${role.code}`}
                            />
                          )}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </div>

                <div className="space-y-2">
                  <Typography className="text-sm font-medium text-neutral-700">
                    Selected Users
                  </Typography>
                  <Search
                    size="md"
                    value={search}
                    onSearch={handleSearch}
                    placeholder="Search selected users..."
                    className="w-full rounded-full shadow-sm border-neutral-200 focus:ring-sky-500 bg-white"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex-1 px-4 pb-4 min-h-0">
            <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden h-full">
              {filteredUsers.length > 0 ? (
                <div className="divide-y divide-neutral-100 overflow-y-auto h-full">
                  {filteredUsers?.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between gap-2 p-3 group hover:bg-neutral-50 transition-all duration-200 transform animate-fade-in"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                          <Typography className="text-sm text-neutral-500">
                            {user.username.charAt(0).toUpperCase()}
                          </Typography>
                        </div>
                        <Typography className="text-sm text-neutral-900">
                          {user.username}
                        </Typography>
                      </div>
                      <IconButton
                        variant="plain"
                        color="neutral"
                        size="sm"
                        icon={faXmark}
                        onClick={() => handleRemoveUser(user)}
                        className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-all duration-200 rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-8 px-4 text-center animate-fade-in">
                  <Icon
                    icon={faSearch}
                    className="text-neutral-300 text-3xl mb-3"
                  />
                  <Typography className="text-sm text-neutral-500">
                    No users match your search
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </div>
      </Drawer.Content>
      <Drawer.Footer className="border-t border-neutral-200 bg-white p-4 flex-none">
        <div className="flex items-center justify-end gap-3 w-full">
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleCancel}
            className="w-full rounded-full"
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            className="w-full rounded-full"
            onClick={handleAdd}
            disabled={!isDirty}
          >
            <Icon icon={faUserPlus} className="mr-2" />
            Assign Role
          </Button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};

export default BulkRoleAddDrawer;
